import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Box, Divider, Paper, Skeleton, Stack, Tooltip, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'

import {
  COLOR_NEGOTIATED,
  NEGOTIATED_JOBS_KPI_INFO,
} from 'components/reports/Metrics/constants/NegotiationOverview'
import { BidNegotiationOverviewDto } from 'generated/graphql'

interface Props {
  data?: BidNegotiationOverviewDto | null
  loading?: boolean
}

export const NegotiatedJobsKpiCard = ({ data, loading }: Props) => (
  <Paper
    variant='outlined'
    sx={{
      p: 2.5,
      borderRadius: 2,
      bgcolor: alpha(COLOR_NEGOTIATED, 0.05),
      borderColor: alpha(COLOR_NEGOTIATED, 0.25),
    }}>
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={{ xs: 2, md: 4 }}
      divider={<Divider orientation='vertical' flexItem />}
      alignItems={{ xs: 'flex-start', md: 'center' }}>
      <Box sx={{ minWidth: { md: 320 } }}>
        <Stack direction='row' spacing={1} alignItems='center' mb={1}>
          <Typography variant='subtitle2' fontWeight={700}>
            Jobs with a negotiated winning bid
          </Typography>
          <Tooltip
            title={NEGOTIATED_JOBS_KPI_INFO}
            slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: 420 } } }}>
            <InfoOutlinedIcon fontSize='small' sx={{ color: 'text.disabled' }} />
          </Tooltip>
        </Stack>

        {loading || !data ? (
          <Skeleton width={160} height={64} />
        ) : (
          <>
            <Typography variant='h2' fontWeight={700} sx={{ color: COLOR_NEGOTIATED }}>
              {`${data.negotiatedPercentage.toLocaleString()}%`}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              <Box component='span' fontWeight={700} color='text.primary'>
                {data.negotiatedJobs.toLocaleString()}
              </Box>
              {' of '}
              <Box component='span' fontWeight={700} color='text.primary'>
                {data.totalJobs.toLocaleString()}
              </Box>
              {' jobs'}
            </Typography>
          </>
        )}
      </Box>

      <Stack direction='row' spacing={2.5} alignItems='center' sx={{ maxWidth: 520 }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            border: `2px solid ${COLOR_NEGOTIATED}`,
            bgcolor: alpha(COLOR_NEGOTIATED, 0.12),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
          <BarChartRoundedIcon sx={{ color: COLOR_NEGOTIATED }} />
        </Box>

        {loading || !data ? (
          <Skeleton width={320} height={48} />
        ) : (
          <Typography variant='body2' color='text.primary'>
            {`${data.negotiatedPercentage.toLocaleString()}% of jobs in this period had a ` +
              'winning bid whose price was changed at least once before allocation.'}
          </Typography>
        )}
      </Stack>
    </Stack>
  </Paper>
)
